import { useState, useEffect, useRef } from 'react';
import PageTransition from '../components/PageTransition';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useMenu } from '../contexts/MenuContext';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Toast, { ToastType } from '../components/Toast';
import { CartItem } from '../types/SpareParts';
import { getCartItems, removeFromCart, updateCartItemQuantity, clearCart, getUserAddress } from '../services/sparePartsService';
import ConfirmationDialog from '../components/ConfirmationDialog';
import ProductImage from '../components/ProductImage';

export default function Cart() {
  const navigate = useNavigate();
  const { userData } = useAuth();
  const { isSidebarOpen, isMobile } = useMenu();
  const queryClient = useQueryClient();
  
  // React Query ile yönetilmeyen state'ler
  const [showClearConfirmation, setShowClearConfirmation] = useState(false);
  const [processingItem, setProcessingItem] = useState<number | null>(null);
  const [isWaitingForAddress, setIsWaitingForAddress] = useState(false);
  const [isComponentMounted, setIsComponentMounted] = useState(false);
  
  // Scroll yönünü takip etmek için state ve ref
  const [showBackButton, setShowBackButton] = useState(true);
  const [showClearButton, setShowClearButton] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const lastScrollTop = useRef(0);
  
  // Toast ile ilgili state değişkenleri
  const [toast, setToast] = useState({
    isVisible: false,
    message: '',
    type: 'info' as ToastType
  });
  
  // Toast gösterme fonksiyonu
  const showToast = (message: string, type: ToastType = 'info') => {
    setToast({
      isVisible: true,
      message,
      type
    });
  };
  
  // Toast kapatma fonksiyonu
  const hideToast = () => {
    setToast(prev => ({
      ...prev,
      isVisible: false
    }));
  };

  // Toplam tutarı hesapla
  const calculateTotal = (items: CartItem[]) => {
    return items.reduce((sum, item) => sum + item.SiparisTutari, 0);
  };

  // React Query ile sepet verilerini çek
  const {
    data: cartData,
    isLoading: loading,
    error: cartError,
    isRefetching: refreshing
  } = useQuery({
    queryKey: ['cart'],
    queryFn: async () => {
      const response = await getCartItems();
      return response;
    },
    enabled: isComponentMounted, // Component mount edildikten sonra çalışsın
    staleTime: 1 * 60 * 1000, // 1 dakika fresh tut (sepet sık değişebilir)
    gcTime: 5 * 60 * 1000, // 5 dakika cache'te tut
  });

  // Sepet verilerini çıkar
  const cartItems = cartData?.data || [];
  const totalAmount = calculateTotal(cartItems);

  // React Query ile kullanıcı adres bilgilerini çek
  const {
    data: addressData,
    isLoading: isLoadingAddress,
    error: addressError
  } = useQuery({
    queryKey: ['userAddress', userData?.User],
    queryFn: async () => {
      if (!userData?.User) throw new Error('Kullanıcı bilgisi bulunamadı');
      const response = await getUserAddress(userData.User);
      if (response.total === 0) {
        throw new Error('Adres bilgisi bulunamadı.');
      }
      return response;
    },
    enabled: isComponentMounted && !!userData?.User, // Component mount edildikten sonra ve user varsa çalışsın
    staleTime: 5 * 60 * 1000, // 5 dakika fresh tut (adres bilgileri sık değişmez)
    gcTime: 10 * 60 * 1000, // 10 dakika cache'te tut
  });

  const userAddress = addressData;

  // Sepet öğesini sil
  const handleRemoveItem = async (itemId: number) => {
    // İlgili sepet öğesini bul
    const cartItem = cartItems.find(item => item.Id === itemId);
    if (!cartItem) {
      showToast('Sepet öğesi bulunamadı.', 'error');
      return;
    }
    
    setProcessingItem(itemId);
    try {
      await removeFromCart(cartItem);
      
      // React Query cache'ini invalidate et - sepet verilerini yenile
      queryClient.invalidateQueries({ queryKey: ['cart'] });
      
      showToast('Ürün sepetten kaldırıldı.', 'success');
    } catch (error) {
      console.error('Ürün silinirken hata:', error);
      showToast('Ürün silinirken bir hata oluştu.', 'error');
    } finally {
      setProcessingItem(null);
    }
  };

  // Miktar güncelle
  const handleQuantityChange = async (itemId: number, newQuantity: number) => {
    if (newQuantity < 1) return;
    
    // İlgili sepet öğesini bul
    const cartItem = cartItems.find(item => item.Id === itemId);
    if (!cartItem) {
      showToast('Sepet öğesi bulunamadı.', 'error');
      return;
    }
    
    setProcessingItem(itemId);
    try {
      await updateCartItemQuantity(cartItem, newQuantity);
      
      // React Query cache'ini invalidate et - sepet verilerini yenile
      queryClient.invalidateQueries({ queryKey: ['cart'] });
      
      showToast('Miktar güncellendi.', 'success');
    } catch (error) {
      console.error('Miktar güncellenirken hata:', error);
      showToast('Miktar güncellenirken bir hata oluştu.', 'error');
    } finally {
      setProcessingItem(null);
    }
  };

  // Sepeti temizle
  const handleClearCart = async () => {
    // Modal'ı hemen kapat
    setShowClearConfirmation(false);
    
    try {
      // API çağrısını yap
      await clearCart();
      
      // React Query cache'ini invalidate et - sepet verilerini yenile
      queryClient.invalidateQueries({ queryKey: ['cart'] });
      
      showToast('Sepet temizlendi.', 'success');
    } catch (error) {
      console.error('Sepet temizlenirken hata:', error);
      showToast('Sepet temizlenirken bir hata oluştu.', 'error');
    }
  };

  // Geri gitme fonksiyonu
  const handleGoBack = () => {
    navigate(-1); // Bir önceki sayfaya git
  };

  // Component mount edildikten sonra flag'i set et
  useEffect(() => {
    setIsComponentMounted(true);
  }, []);

  // İlk yükleme tamamlandığında buton gösterimini aktifleştir
  useEffect(() => {
    if (!loading && isComponentMounted) {
      const timer = setTimeout(() => {
        setIsInitialLoad(false);
      }, 500); // 500ms gecikme
      
      return () => clearTimeout(timer);
    }
  }, [loading, isComponentMounted]);

  // Scroll olayını dinle - butonu gizle/göster
  useEffect(() => {
    const handleScroll = () => {
      const st = window.pageYOffset || document.documentElement.scrollTop;
      const isModalOpen = document.body.hasAttribute('data-modal-open');
      
      // Modal açıksa butonları gizle
      if (isModalOpen) {
        setShowBackButton(false);
        setShowClearButton(false);
        return;
      }
      
      // Modal kapandıysa, mevcut scroll pozisyonuna göre butonların durumunu yeniden değerlendir
      if (lastScrollTop.current === undefined || lastScrollTop.current === null) {
        setShowBackButton(true);
        setShowClearButton(true);
        lastScrollTop.current = st;
        return;
      }
      
      // Butonlar için threshold (10px)
      if (st > lastScrollTop.current + 10) {
        setShowBackButton(false);
        setShowClearButton(false);
      } else if (st < lastScrollTop.current - 10) {
        setShowBackButton(true);
        setShowClearButton(true);
      }
      
      // Son scroll pozisyonunu kaydet
      lastScrollTop.current = st <= 0 ? 0 : st;
    };

    // Modal kapanma durumunu özel olarak kontrol eden fonksiyon
    const handleModalStateChange = () => {
      const isModalOpen = document.body.hasAttribute('data-modal-open');
      
      if (!isModalOpen) {
        // Modal kapandı - butonları tekrar göster
        const st = window.pageYOffset || document.documentElement.scrollTop;
        setShowBackButton(true);
        setShowClearButton(true);
        lastScrollTop.current = st;
        
        // Kısa bir delay ile normal scroll davranışını başlat
        setTimeout(() => {
          handleScroll();
        }, 50);
      } else {
        // Modal açıldı - butonları gizle
        setShowBackButton(false);
        setShowClearButton(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    
    // Body attribute değişikliklerini izle (modal açılıp kapanması için)
    const observer = new MutationObserver(handleModalStateChange);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['data-modal-open']
    });
    
    // İlk kontrol
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      observer.disconnect();
    };
  }, []);

  // Hata durumunda toast göster
  useEffect(() => {
    if (cartError) {
      console.error('Sepet yüklenirken hata:', cartError);
      showToast('Sepet yüklenirken bir hata oluştu.', 'error');
    }
  }, [cartError]);

  useEffect(() => {
    if (addressError) {
      console.error('Adres bilgisi alma hatası:', addressError);
      // Adres hatası için toast gösterme, çünkü bu arka planda çalışır
      // Hata olursa waiting durumunu kapat
      setIsWaitingForAddress(false);
    }
  }, [addressError]);

  // Adres başarıyla yüklendiğinde waiting durumunu kapat
  useEffect(() => {
    if (userAddress && isWaitingForAddress) {
      setIsWaitingForAddress(false);
    }
  }, [userAddress, isWaitingForAddress]);

  return (
    <PageTransition>
      <div className="max-w-4xl mx-auto">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <div className="flex items-center gap-4">
                <div className="flex flex-col md:flex-row md:items-center md:gap-3">
                  <div className="flex items-center gap-3">
                    <div className="hidden md:flex w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl items-center justify-center shadow-lg">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                      </svg>
                    </div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white ml-12 md:ml-0">
                      Sepetim
                    </h1>
                  </div>
                  {!loading && cartItems.length > 0 && (
                    <div className="flex items-center gap-3 ml-12 md:ml-0 mt-1 md:mt-0">
                      <div className="flex items-center gap-1.5">
                        <div className="w-4 h-4 bg-indigo-100 dark:bg-indigo-900/30 rounded-full flex items-center justify-center">
                          <svg className="w-2.5 h-2.5 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                          </svg>
                        </div>
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          {cartItems.length} ürün
                        </span>
                      </div>
                      <div className="w-1 h-1 bg-gray-300 dark:bg-gray-600 rounded-full"></div>
                      <div className="flex items-center gap-1.5">
                        <div className="w-4 h-4 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                          <svg className="w-2.5 h-2.5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                        </div>
                        <span className="text-sm font-semibold text-green-700 dark:text-green-400">
                          ₺{totalAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

          </div>
        </div>

        {/* Floating Geri Butonu - Sadece mobilde görünür */}
        <AnimatePresence>
          {!loading && !isInitialLoad && showBackButton && !(isMobile && isSidebarOpen) && (
            <motion.div
              className="fixed top-4 right-4 z-50 md:hidden"
              initial={{ scale: 0, opacity: 0, y: -20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0, opacity: 0, y: -20 }}
              transition={{
                type: "spring",
                stiffness: 300,
                damping: 20,
                duration: 0.4
              }}
            >
              <motion.button
                onClick={handleGoBack}
                className="relative bg-gradient-to-r from-slate-500 to-slate-600 hover:from-slate-600 hover:to-slate-700 text-white p-2.5 rounded-full shadow-lg shadow-slate-500/25 hover:shadow-xl hover:shadow-slate-500/30 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500"
                aria-label="Geri Git"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                transition={{ type: "spring", stiffness: 400, damping: 17 }}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Floating Temizle Butonu - Mobilde geri butonunun altında, desktop'ta en üstte */}
        <AnimatePresence>
          {!loading && !isInitialLoad && cartItems.length > 0 && showClearButton && !(isMobile && isSidebarOpen) && (
            <motion.div
              className="fixed top-16 md:top-4 right-4 z-50"
              initial={{ scale: 0, opacity: 0, y: -20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0, opacity: 0, y: -20 }}
              transition={{
                type: "spring",
                stiffness: 300,
                damping: 20,
                delay: 0.1
              }}
            >
              <motion.button
                onClick={() => setShowClearConfirmation(true)}
                className="relative bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white p-2.5 rounded-full shadow-lg shadow-red-500/25 hover:shadow-xl hover:shadow-red-500/30 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                aria-label="Sepeti Temizle"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                transition={{ type: "spring", stiffness: 400, damping: 17 }}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Ana İçerik */}
        <div className="bg-white dark:bg-gray-800 shadow-lg rounded-xl border border-gray-200 dark:border-gray-700">
          {loading ? (
            <div className="flex items-center justify-center p-12">
              <div className="flex flex-col items-center">
                <svg className="animate-spin h-8 w-8 text-indigo-600 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <p className="text-gray-600 dark:text-gray-400">Sepet yükleniyor...</p>
              </div>
            </div>
          ) : cartItems.length === 0 ? (
            <div className="p-4 sm:p-8">
              {/* Sepet boş durumu */}
              <div className="text-center py-12 sm:py-16">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="flex flex-col items-center"
                >
                  <div className="w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                    <svg className="w-10 h-10 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                    Sepetiniz Boş
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-md leading-relaxed">
                    Henüz sepetinize ürün eklememişsiniz. Yedek parça koleksiyonumuzu keşfedin ve ihtiyacınız olan parçaları bulun.
                  </p>
                  <button
                    onClick={() => navigate('/spare-parts')}
                    className="inline-flex items-center gap-2 font-semibold py-3 px-6 rounded-xl transition-all duration-200 text-sm bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95 text-white relative overflow-hidden before:absolute before:inset-0 before:bg-gradient-to-r before:from-white/10 before:via-transparent before:to-white/10 before:translate-x-[-100%] hover:before:translate-x-[100%] before:transition-transform before:duration-700"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                    </svg>
                    Yedek Parça Sayfasına Git
                  </button>
                </motion.div>
              </div>
            </div>
          ) : (
            <div className="p-4 sm:p-6">
              {/* Sepet Öğeleri */}
              <div className="space-y-4 mb-6">
                <AnimatePresence>
                  {cartItems.map((item, index) => (
                    <motion.div
                      key={item.Id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.3, delay: index * 0.1 }}
                      className="border border-gray-200 dark:border-gray-700 rounded-xl bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 shadow-sm hover:shadow-md transition-all duration-200"
                    >
                      <div className="rounded-xl p-4 sm:p-5 border border-white dark:border-gray-700">
                        <div className="space-y-3">
                        {/* Üst Kısım: Görsel, Bilgiler ve Silme Butonu */}
                        <div className="flex items-start space-x-3">
                          {/* Ürün Görseli */}
                          <div className="flex-shrink-0">
                            <ProductImage
                              imageId={item.Gorsel}
                              alt={item.Ad}
                              size="w-16 h-16"
                              iconSize="w-8 h-8"
                              containerClassName="p-1"
                            />
                          </div>

                          {/* Ürün Bilgileri */}
                          <div className="flex-1 min-w-0">
                            <h3 className="text-base sm:text-lg font-medium text-gray-900 dark:text-white line-clamp-2">
                              {item.Ad}
                            </h3>
                            <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-1">
                              Kod: {item.Kod}
                            </p>
                            <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                              Grup: {item.GrupAdi}
                            </p>
                            {item.PromosyonluMu && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300 mt-1">
                                Promosyonlu
                              </span>
                            )}
                          </div>

                          {/* Silme Butonu */}
                          <div className="flex-shrink-0">
                            <button
                              onClick={() => handleRemoveItem(item.Id)}
                              disabled={processingItem === item.Id}
                              className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 p-2 rounded-lg transition-colors disabled:opacity-50"
                            >
                              {processingItem === item.Id ? (
                                <svg className="animate-spin w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 004 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                              ) : (
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              )}
                            </button>
                          </div>
                        </div>

                        {/* Alt Kısım: Miktar Kontrolü ve Fiyat */}
                        <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-600">
                          {/* Miktar Kontrolü */}
                          <div className="flex items-center space-x-3">
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Miktar:</span>
                            <div className="flex items-center bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                              <button
                                onClick={() => handleQuantityChange(item.Id, item.Miktar - 1)}
                                disabled={item.Miktar <= 1 || processingItem === item.Id}
                                className="w-9 h-9 rounded-l-lg flex items-center justify-center text-gray-600 dark:text-gray-400 bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-700 dark:via-gray-700/90 dark:to-gray-600 hover:from-gray-50 hover:via-gray-100 hover:to-gray-200 dark:hover:from-gray-600 dark:hover:via-gray-600/90 dark:hover:to-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 12H4" />
                                </svg>
                              </button>
                              
                              <div className="w-12 h-9 flex items-center justify-center text-base font-semibold text-gray-900 dark:text-white bg-white dark:bg-gray-800 border-x border-gray-200 dark:border-gray-600">
                                {item.Miktar}
                              </div>
                              
                              <button
                                onClick={() => handleQuantityChange(item.Id, item.Miktar + 1)}
                                disabled={item.Miktar >= item.FiiliStok || processingItem === item.Id}
                                className="w-9 h-9 rounded-r-lg flex items-center justify-center text-gray-600 dark:text-gray-400 bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-700 dark:via-gray-700/90 dark:to-gray-600 hover:from-gray-50 hover:via-gray-100 hover:to-gray-200 dark:hover:from-gray-600 dark:hover:via-gray-600/90 dark:hover:to-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                </svg>
                              </button>
                            </div>
                          </div>

                          {/* Fiyat */}
                          <div className="text-right">
                            <div className="text-lg font-bold text-gray-900 dark:text-white">
                              ₺{item.SiparisTutari.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                                                              ₺{item.Fiyat.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} x {item.Miktar}
                            </div>
                          </div>
                        </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>

              {/* Toplam ve Sipariş */}
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 shadow-sm hover:shadow-md transition-all duration-200">
                <div className="rounded-xl p-6 border border-white dark:border-gray-700">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-gradient-to-br from-indigo-50 via-blue-50 to-indigo-100 dark:from-gray-700 dark:via-gray-700/80 dark:to-gray-600 rounded-lg flex items-center justify-center mr-3 shadow-sm border border-indigo-100 dark:border-gray-600">
                      <svg className="w-5 h-5 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-gray-900 dark:text-white">
                        <span className="hidden sm:inline">Toplam Tutar</span>
                        <span className="sm:hidden">Toplam</span>
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {cartItems.length} ürün
                      </p>
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                    ₺{totalAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    onClick={() => navigate('/spare-parts')}
                    className="flex-1 font-semibold py-3 px-5 rounded-xl transition-all duration-200 text-sm bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95 text-white relative overflow-hidden before:absolute before:inset-0 before:bg-gradient-to-r before:from-white/10 before:via-transparent before:to-white/10 before:translate-x-[-100%] hover:before:translate-x-[100%] before:transition-transform before:duration-700"
                  >
                    Alışverişe Devam Et
                  </button>
                  
                  <button
                    onClick={() => {
                      if (!userData?.User) {
                        showToast('Kullanıcı bilgisi bulunamadı.', 'error');
                        return;
                      }
                      
                      // Adres bilgileri zaten yüklü mü kontrol et
                      if (userAddress) {
                        // Hemen OrderAddress sayfasına yönlendir
                        navigate('/order-address', {
                          state: {
                            userAddress: userAddress,
                            cartItems: cartItems,
                            totalAmount: totalAmount
                          }
                        });
                      } else if (addressError) {
                        // Adres yükleme hatası varsa göster
                        showToast('Adres bilgisi bulunamadı.', 'error');
                      } else if (isLoadingAddress) {
                        // Hala yükleniyor - butonda waiting durumunu göster
                        setIsWaitingForAddress(true);
                        showToast('Adres bilgileri yükleniyor, lütfen bekleyin...', 'info');
                      } else {
                        // Adres yüklemeyi yeniden dene - butonda waiting durumunu göster
                        setIsWaitingForAddress(true);
                        // React Query otomatik olarak yeniden deneyecek
                        showToast('Adres bilgileri yükleniyor...', 'info');
                      }
                    }}
                    disabled={isWaitingForAddress}
                    className={`flex-1 font-semibold py-3 px-5 rounded-xl transition-all duration-200 text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 shadow-lg text-white flex items-center justify-center ${
                      isWaitingForAddress
                        ? 'bg-gray-400 cursor-not-allowed' 
                        : 'bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 hover:shadow-xl transform hover:scale-105 active:scale-95 relative overflow-hidden before:absolute before:inset-0 before:bg-gradient-to-r before:from-white/10 before:via-transparent before:to-white/10 before:translate-x-[-100%] hover:before:translate-x-[100%] before:transition-transform before:duration-700'
                    }`}
                  >
                    {isWaitingForAddress ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Adres Bilgileri Alınıyor...
                      </>
                    ) : (
                      'Sipariş Ver'
                    )}
                  </button>
                </div>
                </div>
              </div>
             </div>
           )}
        </div>

        {/* Toast */}
        <Toast
          isVisible={toast.isVisible}
          message={toast.message}
          type={toast.type}
          onClose={hideToast}
        />

        {/* Sepet Temizleme Onay Dialogu */}
        <ConfirmationDialog
          isOpen={showClearConfirmation}
          onCancel={() => setShowClearConfirmation(false)}
          onConfirm={handleClearCart}
          title="Sepeti Temizle"
          message="Sepetteki tüm ürünleri silmek istediğinize emin misiniz? Bu işlem geri alınamaz."
          confirmText="Evet, Temizle"
          cancelText="İptal"
          autoClose={true}
        />
      </div>
    </PageTransition>
  );
} 